import axios from "./axios_interceptor";
import { get_jwt_token, loginRedirect } from "./helper_funtions";

const _headers = () => {
  const token = get_jwt_token();
  loginRedirect(token);
  return {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };
};

// ── Scan Configurations ───────────────────────────────────────────────────

export const getScanConfigs = async () => {
  try {
    const response = await axios.get("/credential-scanning/configs/", _headers());
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const getScanConfigById = async (id) => {
  try {
    const response = await axios.get(
      `/credential-scanning/configs/?id=${id}`,
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const createScanConfig = async (payload) => {
  try {
    const response = await axios.post(
      "/credential-scanning/configs/",
      payload,
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const updateScanConfig = async (payload) => {
  try {
    const response = await axios.put(
      "/credential-scanning/configs/",
      payload,
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const deleteScanConfig = async (id) => {
  try {
    const response = await axios.delete("/credential-scanning/configs/", {
      ..._headers(),
      data: { id },
    });
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

// ── Git Repositories ──────────────────────────────────────────────────────

export const getGitRepos = async (scanConfigId) => {
  try {
    let url = "/credential-scanning/git-repos/";
    if (scanConfigId) url += `?scan_config_id=${scanConfigId}`;
    const response = await axios.get(url, _headers());
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const createGitRepo = async (payload) => {
  try {
    const response = await axios.post(
      "/credential-scanning/git-repos/",
      payload,
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const updateGitRepo = async (payload) => {
  try {
    const response = await axios.put(
      "/credential-scanning/git-repos/",
      payload,
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const deleteGitRepo = async (id) => {
  try {
    const response = await axios.delete("/credential-scanning/git-repos/", {
      ..._headers(),
      data: { id },
    });
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const testGitConnection = async (repoId) => {
  try {
    const response = await axios.post(
      "/credential-scanning/git-repos/test-connection/",
      { repo_id: repoId },
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

// ── Scans ─────────────────────────────────────────────────────────────────

export const startScan = async (configId) => {
  try {
    const response = await axios.post(
      "/credential-scanning/start-scan/",
      { config_id: configId },
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const deleteScan = async (id) => {
  try {
    const response = await axios.delete(
      `/credential-scanning/scan-history/?id=${id}`,
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const stopScan = async (id) => {
  try {
    const response = await axios.post(
      "/credential-scanning/stop-scan/",
      { id },
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const getScanHistory = async ({ configId, scanId, page = 1 } = {}) => {
  try {
    const params = new URLSearchParams();
    if (configId) params.append("config_id", configId);
    if (scanId) params.append("id", scanId);
    if (page > 1) params.append("page", page);
    const url = `/credential-scanning/scan-history/${
      params.toString() ? "?" + params.toString() : ""
    }`;
    const response = await axios.get(url, _headers());
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const getScanLogs = async (scanId, sinceId = 0) => {
  try {
    const response = await axios.get(
      `/credential-scanning/scan-logs/?scan_id=${scanId}&since_id=${sinceId}`,
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

// ── Findings ──────────────────────────────────────────────────────────────

export const getFindings = async ({
  severity,
  status,
  source_type,
  scan_id,
  asset_id,
  credential_type,
  search,
  file_path,
  page = 1,
  page_size,
} = {}) => {
  try {
    const params = new URLSearchParams();
    if (severity) params.append("severity", severity);
    if (status) params.append("status", status);
    if (source_type) params.append("source_type", source_type);
    if (scan_id) params.append("scan_id", scan_id);
    if (asset_id) params.append("asset_id", asset_id);
    if (credential_type) params.append("credential_type", credential_type);
    if (search) params.append("search", search);
    // Exact-match file_path is used by the 'Files Scanned' tab click-through
    // so the user lands on findings for the specific file they clicked.
    if (file_path) params.append("file_path", file_path);
    params.append("page", page);
    if (page_size) params.append("page_size", page_size);
    const url = `/credential-scanning/findings/?${params.toString()}`;
    const response = await axios.get(url, _headers());
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const getFindingById = async (id) => {
  try {
    const response = await axios.get(
      `/credential-scanning/findings/?id=${id}`,
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const acknowledgeFinding = async (id, notes = "") => {
  try {
    const response = await axios.post(
      `/credential-scanning/findings/${id}/acknowledge/`,
      { remediation_notes: notes },
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const markFindingFalsePositive = async (id, notes = "") => {
  try {
    const response = await axios.post(
      `/credential-scanning/findings/${id}/mark-false-positive/`,
      { remediation_notes: notes },
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const bulkRecheckFindings = async (findingIds) => {
  try {
    const response = await axios.post(
      `/credential-scanning/findings/bulk-recheck/`,
      { finding_ids: findingIds },
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data?.error || err.message };
  }
};

export const recheckFinding = async (id) => {
  try {
    const response = await axios.post(
      `/credential-scanning/findings/${id}/recheck/`,
      {},
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data?.error || err.message };
  }
};

export const bulkFindingAction = async (findingIds, action, notes = "") => {
  try {
    const response = await axios.post(
      "/credential-scanning/findings/bulk-action/",
      { finding_ids: findingIds, action, remediation_notes: notes },
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

export const exportFindings = async ({ severity, status } = {}) => {
  const token = get_jwt_token();
  loginRedirect(token);
  const params = new URLSearchParams();
  if (severity) params.append("severity", severity);
  if (status) params.append("status", status);
  const url = `/credential-scanning/export/${
    params.toString() ? "?" + params.toString() : ""
  }`;
  // Return URL for download link (browser handles the CSV download)
  return url;
};

export const getScanDetails = async (scanId) => {
  try {
    const response = await axios.get(
      `/credential-scanning/scan-details/?scan_id=${scanId}`,
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

// ── Scanned files (one row per file the scanner actually read) ───────────

export const getScannedFiles = async ({
  scan_id,
  source_type,
  search,
  with_findings_only,
  page = 1,
  page_size,
} = {}) => {
  if (!scan_id) {
    return { data: null, error: "scan_id is required" };
  }
  try {
    const params = new URLSearchParams();
    params.append("scan_id", scan_id);
    if (source_type) params.append("source_type", source_type);
    if (search) params.append("search", search);
    if (with_findings_only) params.append("with_findings_only", "true");
    params.append("page", page);
    if (page_size) params.append("page_size", page_size);
    const response = await axios.get(
      `/credential-scanning/scanned-files/?${params.toString()}`,
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};

// ── Dashboard Summary ─────────────────────────────────────────────────────

export const getDashboardSummary = async () => {
  try {
    const response = await axios.get(
      "/credential-scanning/dashboard-summary/",
      _headers()
    );
    return { data: response.data, error: null };
  } catch (err) {
    return { data: null, error: err?.response?.data || err.message };
  }
};
